import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { api, formatApiError } from "@/auth/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true); setError(null);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6 py-16 font-body text-[#121212]">
      <SEO title="Reset Password — The China Explorer" description="Reset your B2B account password." path="/reset-password" />
      <div className="w-full max-w-md">
        <p className="text-center font-heading text-xl text-[#0E2A5C] mb-8">The China Explorer</p>

        {done ? (
          <div className="bg-white border border-[#EAE6DF] p-8 md:p-10 text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto mb-4 text-emerald-600" size={30}/>
            <h1 className="font-heading text-2xl text-[#0E2A5C]">Password updated</h1>
            <p className="mt-3 text-sm text-[#4A4A4A]">You can now sign in with your new password.</p>
            <Link to="/login" data-testid="reset-goto-login"
              className="mt-6 inline-flex items-center gap-2 bg-[#C62828] hover:bg-[#B71C1C] text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
              Sign In <ArrowRight size={13}/>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#EAE6DF] p-8 md:p-10" data-testid="reset-form">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ Password Reset</p>
            <h1 className="font-heading text-3xl mt-3 text-[#0E2A5C]">Choose a new password</h1>
            <div className="mt-8 grid gap-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">New Password</label>
                <input type="password" value={password} required autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input"
                  className="mt-1.5 w-full px-4 py-3.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">Confirm Password</label>
                <input type="password" value={confirm} required autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
                  className="mt-1.5 w-full px-4 py-3.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
              </div>
            </div>
            {error && <p className="mt-4 text-sm text-[#C62828]" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="mt-7 w-full h-[52px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 transition-colors">
              {loading ? <Loader2 size={16} className="animate-spin"/> : <>Update Password <ArrowRight size={14}/></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
